import Draw, { DrawEvent } from "ol/interaction/Draw";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { getLength } from "ol/sphere";
import { Fill, RegularShape, Stroke, Style, Text } from "ol/style";
import CircleStyle from "ol/style/Circle";
import { useContext, useEffect, useMemo, useState } from "react";
import Container from "./MapPortal";
import MapContext from "./MapContext";
import { Geometry, LineString, MultiPolygon, Point, Polygon } from "ol/geom";
import { StyleLike } from "ol/style/Style";
import { Feature } from "ol";
import Button from "../Buttons/Button";

export enum MeasureMode {
  DISTANCE = "measure_distance",
  AREA = "measure_area",
}

const style = new Style({
  fill: new Fill({
    color: "rgba(255, 255, 255, 0.2)",
  }),
  stroke: new Stroke({
    color: "rgba(0, 0, 0, 0.5)",
    lineDash: [10, 10],
    width: 2,
  }),
  image: new CircleStyle({
    radius: 5,
    stroke: new Stroke({
      color: "rgba(0, 0, 0, 0.7)",
    }),
    fill: new Fill({
      color: "rgba(255, 255, 255, 0.2)",
    }),
  }),
});

export default function MeasureInteraction() {
  const { map } = useContext(MapContext);
  const [measureMode] = useState<MeasureMode>(MeasureMode.DISTANCE);
  const vectorSource = useMemo(() => new VectorSource(), []);
  const MeasureStyle = useMemo(
    () =>
      createLabeledStyle(
        [style],
        [],
        measureMode === MeasureMode.DISTANCE ? "LineString" : "Polygon"
      ),
    [measureMode]
  );
  const vectorLayer = useMemo(
    () =>
      new VectorLayer({
        zIndex: 99,
        source: vectorSource,
        // @ts-ignore
        style: MeasureStyle,
      }),
    [vectorSource, MeasureStyle]
  );

  useMemo(() => {
    if (vectorLayer) map?.addLayer(vectorLayer);
    return () => {
      if (vectorLayer) map?.removeLayer(vectorLayer);
    };
  }, [map, vectorLayer]);

  useEffect(() => {
    return () => {
      vectorSource?.clear();
    };
  }, [vectorSource, measureMode]);

  const drawInteraction = useMemo(() => {
    return new Draw({
      source: vectorSource,
      type: measureMode === MeasureMode.DISTANCE ? "LineString" : "Polygon",
      // @ts-ignore
      style: MeasureStyle,
    });
  }, [measureMode, vectorSource, MeasureStyle]);

  useEffect(() => {
    function onDrawStart(e: DrawEvent) {
      vectorSource?.clear();
    }
    function onDrawEnd(e: DrawEvent) {}

    drawInteraction && drawInteraction?.on("drawstart", onDrawStart);
    drawInteraction && drawInteraction?.on("drawend", onDrawEnd);
    drawInteraction && map?.addInteraction(drawInteraction);
    return () => {
      drawInteraction && drawInteraction?.un("drawstart", onDrawStart);
      drawInteraction && drawInteraction?.un("drawend", onDrawEnd);
      drawInteraction && map?.removeInteraction(drawInteraction);
    };
  }, [map, drawInteraction, vectorSource]);

  return (
    <Container meta>
      <div
        style={{
          maxWidth: 250,
          backgroundColor: "rgba(0,0,0,0.6)",
          borderRadius: 16,
          display: "flex",
          flexDirection: "column",
          padding: 36,
          paddingTop: 21,
          paddingBottom: 21,
          gap: 16,
        }}
      >
        <span
          style={{
            fontFamily: "Roboto",
            fontWeight: 700,
            fontSize: 30,
            color: "#FFF",
          }}
        >
          Afstand meten
        </span>
        <span
          style={{
            fontFamily: "Roboto",
            fontWeight: 400,
            fontSize: 18,
            color: "#FFF",
          }}
        >
          {measureMode === MeasureMode.DISTANCE
            ? "Zet punten op de kaart en meet de lengte van de lijn. Eindig met dubbelklik."
            : "Zet punten op de kaart en meet de oppervlakte van de vorm. Eindig met dubbelklik of annuleer."}
        </span>
        <Button
          label="Verwijder lijn"
          onClick={() => {
            drawInteraction?.abortDrawing();
            vectorSource?.clear();
          }}
        />
      </div>
    </Container>
  );
}

function createLabeledStyle(
  modifyStyle: StyleLike[],
  baseStyle?: StyleLike[],
  geometryType?: string
): (feature: Feature<Geometry>) => StyleLike[] {
  return (feature: Feature<Geometry>) => {
    if (baseStyle?.length && !feature.getProperties().modify) return baseStyle;
    const labelStyle = new Style({
      zIndex: 15,
      text: new Text({
        font: "14px Calibri,sans-serif",
        fill: new Fill({
          color: "rgba(255, 255, 255, 1)",
        }),
        backgroundFill: new Fill({
          color: "rgba(0, 0, 0, 0.7)",
        }),
        padding: [3, 3, 3, 3],
        textBaseline: "bottom",
        offsetY: -15,
      }),
      image: new RegularShape({
        radius: 8,
        points: 3,
        angle: Math.PI,
        displacement: [0, 10],
        fill: new Fill({
          color: "rgba(0, 0, 0, 0.7)",
        }),
      }),
    });

    const segmentStyle = new Style({
      zIndex: 14,
      text: new Text({
        font: "12px Calibri,sans-serif",
        fill: new Fill({
          color: "rgba(255, 255, 255, 1)",
        }),
        backgroundFill: new Fill({
          color: "rgba(0, 0, 0, 0.4)",
        }),
        padding: [2, 2, 2, 2],
        textBaseline: "bottom",
        offsetY: -12,
      }),
      image: new RegularShape({
        radius: 6,
        points: 3,
        angle: Math.PI,
        displacement: [0, 8],
        fill: new Fill({
          color: "rgba(0, 0, 0, 0.4)",
        }),
      }),
    });
    let segmentStyles = [segmentStyle];

    let styles = [...modifyStyle];
    const geometry = feature.getGeometry()?.clone() as Geometry;
    const type = geometry?.getType();
    let point, label, line;
    if (type === "Polygon") {
      point = (geometry as Polygon).getInteriorPoint();
      label = displayArea(getPolygonArea(geometry as Polygon));
      if (feature.getProperties().startPoint) {
        let startCoord = (feature.getProperties().startPoint as Feature<Point>)
          .getGeometry()
          ?.getCoordinates() as number[];
        let coords = (geometry as Polygon).getCoordinates()[0] as number[][];
        let index = 0;
        let lineCoords = [];
        for (let coord of coords) {
          if (coord[0] === startCoord[0] && coord[1] === startCoord[1]) {
            let startIx = index + 1,
              endIx = index - 1;
            if (index === 0) {
              startIx = 1;
              endIx = coords.length - 2;
            }
            lineCoords.push(coords[startIx]);
            lineCoords.push(coords[index]);
            lineCoords.push(coords[endIx]);
            break;
          }
          index++;
        }
        if (lineCoords?.length) line = new LineString(lineCoords);
      } else {
        line = new LineString((geometry as Polygon).getCoordinates()[0]);
      }
    } else if (type === "LineString" && geometryType === "LineString") {
      point = new Point((geometry as LineString).getLastCoordinate());
      label = displayDistance(getLength(geometry));
      line = geometry;
    }
    let showLabel = Boolean(point && label);
    if (line) {
      let count = 0;
      (line as LineString).forEachSegment(function (a, b) {
        const segment = new LineString([a, b]);
        const segmentLabel = displayDistance(getLength(segment));
        if (segmentStyles.length - 1 < count) {
          segmentStyles.push(segmentStyle.clone());
        }
        const segmentPoint = new Point(segment.getCoordinateAt(0.5));
        segmentStyles[count].setGeometry(segmentPoint);
        segmentStyles[count].getText()?.setText(segmentLabel.toString());
        styles.push(segmentStyles[count]);
        count++;
      });
      // in the case of; 1 point, 1 segment and 1 segment abel, hide the area label.
      showLabel = showLabel && Boolean(styles.length > 2);
    }
    if (point) labelStyle.setGeometry(point as Point);
    if (label) labelStyle.getText()?.setText(label?.toString());
    if (showLabel) styles.push(labelStyle);
    return styles;
  };
}

/** Display a formatted distance. Either meters (when distance is < 1000) or kilometers.
 * @example locale = 'nl'; formatLength(484) === '484m'
 * @example locale = 'nl'; formatArea(34000) === '34,00km'
 * @example locale = 'nl'; formatArea(1,050004) === '1,05m'
 */
export function displayDistance(meters: number): string {
  if (meters >= 1000) {
    return `${formatArea(
      Math.round((meters / 1000 + Number.EPSILON) * 100) / 100
    )} km`;
  }
  return `${formatArea(Math.round((meters + Number.EPSILON) * 10) / 10)} m`;
}

interface ToLocaleStringOptions {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

export function formatArea(
  number: number,
  options?: ToLocaleStringOptions
): string {
  return Number(
    Math.round((number + Number.EPSILON) * 10000) / 10000
  ).toLocaleString(undefined, {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
    ...options,
  });
}

/** Display a formatted area. Either square meter (when area is < 1 ha) or hectare.
 * @example locale = 'nl'; displayArea(3.538484) === '3,5385 ha'
 * @example locale = 'nl'; displayArea(1.0) === '1,0000 ha'
 */
export function displayArea(number: number): string {
  return `${formatArea(number)} ha`;
}
function getPolygonArea(geometry: Geometry): number {
  return (
    ((
      geometry?.clone()?.transform("EPSG:3857", "EPSG:28992") as
        | Polygon
        | MultiPolygon
        | undefined
    )?.getArea() || 0) / 10000
  );
}
